<template>
  <app-page
    :title="`Batch #${batch?.batch_number || ''}`"
    subtitle="View details about this batch"
    icon="rotate"
    back-route="/one-time-payments"
  >
    <div v-if="batch">
      <div class="batch-details">
        <p><strong>Status:</strong> {{ batch.status }}</p>
        <p><strong>Created at:</strong> {{ new Date(batch.created_at).toLocaleString() }}</p>
        <p v-if="batch.submitted_at">
          <strong>Submitted at:</strong>
          {{ batch.submitted_at ? new Date(batch.submitted_at).toLocaleString() : 'Not yet submitted' }}
        </p>
        <h4 class="mt-3">Billing</h4>
        <p>
          Your billing method is via {{ batch.billing_type.toLowerCase() }}.
          <span v-if="batch.billing_type.toLowerCase() === 'ACH'">
            Your ACH account ends in {{ batch.ach_account_number.slice(-4) }}
          </span>
          <span v-if="batch.billing_type.toLowerCase() === 'invoice'">
            You can now view, download and settle your invoice.
            <SmeButtonLink @click="downloadInvoice">Download your invoice now</SmeButtonLink>
          </span>
        </p>
        <div class="mt-3">
          <h3>Payments</h3>
          <div v-if="batch.payments.length !== 0">
            <b-table :items="batch.payments" :fields="FIELDS">
              <template v-slot:cell(payment_type)="data">
                <PaymentTypePill :reason="data.item.payment_type" :reason-options="paymentReasons" />
              </template>
            </b-table>
          </div>
          <div v-else>No payments in this batch</div>
        </div>
      </div>
    </div>
    <div v-else>Loading...</div>
  </app-page>
</template>

<script setup>
import { ref, watch } from 'vue';
import AppPage from '@/components/AppPage.vue';
import { useRoute } from 'vue-router/composables';
import { toCurrency } from '@/utils';
import PaymentTypePill from '@/pages/one-time-payments/components/payment-type-pill.vue';
import { useQuery } from '@tanstack/vue-query';
import { getOffCycleConfig, getOffCyclePayment } from '@/queries/one-time-payments';
import SmeButtonLink from '@/components/atoms/SmeButtonLink.vue';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import State from '@/state/State';

const batch = ref(null);
const route = useRoute();
const batchId = ref(route.params.batchId);
const paymentReasons = ref([]);

const getPaymentQuery = useQuery(getOffCyclePayment(batchId));
const offCycleConfig = useQuery(getOffCycleConfig());

watch([getPaymentQuery.fetchStatus, getPaymentQuery.isPending], () => {
  if (getPaymentQuery.data.value) {
    batch.value = getPaymentQuery.data.value;
  }
});

watch([offCycleConfig.fetchStatus, offCycleConfig.isPending], () => {
  if (offCycleConfig.data.value) {
    paymentReasons.value = offCycleConfig.data.value.supported_payment_types;
  }
});

const FIELDS = [
  { key: 'employee_code', label: 'Employee Code' },
  { key: 'employee_email', label: 'Email' },
  { key: 'status', label: 'Status' },
  { key: 'payment_type', label: 'Reason' },
  { key: 'amount', label: 'Amount', formatter: value => toCurrency(value, 'USD') },
];

const exportPDF = async () => {
  const doc = new jsPDF();

  doc.setFontSize(18);
  doc.text('Invoice', 14, 20);
  doc.setFontSize(10);
  doc.text('INV001', 14, 25);

  doc.setFontSize(14);
  doc.text('7 Rathbourne Street', 14, 40);
  doc.text('London, UK', 14, 45);

  doc.text('BILL TO', 150, 42);
  doc.text(State.state.company.name, 150, 50);

  doc.setFontSize(10);
  doc.text('10 Somewhere Street', 150, 55);
  doc.text('USA', 150, 60);

  doc.setFontSize(14);
  doc.text('Due date: 11/23/2024', 150, 80);

  autoTable(doc, {
    head: [FIELDS.map(field => field.label)],
    body: batch.value.payments.map(payment => FIELDS.map(field => payment[field.key])),
    startY: 90,
  });
  doc.text('Total: $300', 14, 130);

  doc.save(`invoice1.pdf`);
};

const downloadInvoice = () => {
  exportPDF();
};
</script>

<style scoped>
.payment-item::marker {
  content: '';
}
</style>
